// Cooperativas — DATOS REALES (tabla cooperativas + conteos de GET
// organizacion/resumen). Antes usaba mockData con fondos y proyectos
// inventados; lo que no exista en la base de datos no se muestra.
//
// Alcance: SuperAdmin / confederación ve todas; federación ve las suyas;
// cooperativa y afiliado ven solo la propia. El registro y la edición de una
// cooperativa (asistente de 4 pasos) viven en «Administración de la Red →
// Estructura».

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Business as BusinessIcon, Groups as GroupsIcon, Paid as PaidIcon, Search as SearchIcon } from '@mui/icons-material';
import useRedReal from '../hooks/useRedReal';
import { useAuth } from '../context/AuthContext';
import { organizacionApi } from '../services/coopnetApi';
import { fecha, fmt, moneda } from '../utils/formato';

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 2 });

const ETIQUETA_DOCS = {
  COMPLETO: ['Completos', 'success'],
  PENDIENTE_CEDULA_REP: ['Falta cédula', 'warning'],
  PENDIENTE_DOCUMENTOS: ['Pendientes', 'warning'],
};

const Dato = ({ titulo, valor }) =>
  valor ? (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {titulo}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Box>
  ) : null;

const Cooperativas = () => {
  const { cooperativas, federaciones, conteos, cargando, error, recargar, nombrePais } = useRedReal();
  const { user, contexto } = useAuth();
  const [busca, setBusca] = useState('');
  const [fedFiltro, setFedFiltro] = useState('');
  const [estado, setEstado] = useState('activas');
  const [detalle, setDetalle] = useState(null);
  const [docs, setDocs] = useState({ cargando: false, error: '', items: [] });

  const fedPorId = useMemo(() => new Map(federaciones.map((f) => [f.id, f])), [federaciones]);

  // Alcance según la vista activa.
  const alcance = useMemo(() => {
    if (user?.nivel === 'cooperativa' || user?.nivel === 'afiliado') return { cooperativaId: contexto?.cooperativa?.id || '__ninguna__' };
    if (user?.nivel === 'federacion') return { federacionId: contexto?.federacion?.id || '__ninguna__' };
    return {};
  }, [user?.nivel, contexto?.cooperativa?.id, contexto?.federacion?.id]);

  const visibles = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return cooperativas
      .filter((k) => !alcance.cooperativaId || k.id === alcance.cooperativaId)
      .filter((k) => !alcance.federacionId || k.federacionId === alcance.federacionId)
      .filter((k) => !fedFiltro || k.federacionId === fedFiltro)
      .filter((k) => (estado === 'todas' ? true : estado === 'activas' ? k.activa !== false : k.activa === false))
      .filter((k) => !t || [k.nombre, k.siglas, k.nit].filter(Boolean).join(' ').toLowerCase().includes(t))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [cooperativas, alcance, fedFiltro, estado, busca]);

  const suma = (campo) => visibles.reduce((t, k) => t + (conteos.cooperativas.get(k.id)?.[campo] || 0), 0);

  const stats = [
    { titulo: 'Cooperativas', valor: fmt(visibles.length), icono: BusinessIcon, color: '#6A1B9A' },
    { titulo: 'Afiliados activos', valor: fmt(suma('afiliados')), icono: GroupsIcon, color: '#2E7D32' },
    { titulo: 'Aporte mensual', valor: moneda(suma('aporteMensual')), icono: PaidIcon, color: '#0D47A1' },
  ];

  // Documentos de la cooperativa abierta (solo los roles con permiso los ven).
  useEffect(() => {
    if (!detalle) return undefined;
    let vivo = true;
    setDocs({ cargando: true, error: '', items: [] });
    organizacionApi
      .listarDocumentosCooperativa(detalle.id)
      .then((d) => vivo && setDocs({ cargando: false, error: '', items: Array.isArray(d) ? d : [] }))
      .catch((e) => vivo && setDocs({ cargando: false, error: e.message || 'No se pudieron cargar los documentos.', items: [] }));
    return () => {
      vivo = false;
    };
  }, [detalle]);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Cooperativas
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Primer piso de la estructura cooperativa
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={recargar}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ ...grid(220), mb: 3 }}>
            {stats.map((s) => (
              <Card key={s.titulo}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${s.color}1A`, color: s.color, display: 'flex' }}>
                    <s.icono />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: s.color }}>
                      {s.valor}
                    </Typography>
                    <Typography variant="body2">{s.titulo}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <TextField
              size="small"
              placeholder="Buscar por nombre, siglas o NIT"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            {!alcance.cooperativaId && !alcance.federacionId && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Federación</InputLabel>
                <Select label="Federación" value={fedFiltro} onChange={(e) => setFedFiltro(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {federaciones.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Estado</InputLabel>
              <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
                <MenuItem value="activas">Activas</MenuItem>
                <MenuItem value="inactivas">Inactivas</MenuItem>
                <MenuItem value="todas">Todas</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {visibles.length === 0 ? (
            <Typography color="text.secondary">
              {cooperativas.length === 0
                ? 'Aún no hay cooperativas. Regístralas o impórtalas desde «Administración de la Red».'
                : 'No hay cooperativas que coincidan con el filtro.'}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cooperativa</TableCell>
                    <TableCell>Federación</TableCell>
                    <TableCell>NIT</TableCell>
                    <TableCell align="right">Afiliados / cupo</TableCell>
                    <TableCell align="right">Aporte mensual</TableCell>
                    <TableCell>Documentos</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibles.map((k) => {
                    const n = conteos.cooperativas.get(k.id) || {};
                    const [textoDocs, colorDocs] = ETIQUETA_DOCS[k.estadoDocumentos] || [k.estadoDocumentos, 'default'];
                    return (
                      <TableRow key={k.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetalle(k)}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {k.nombre}
                          </Typography>
                          {k.siglas && (
                            <Typography variant="caption" color="text.secondary">
                              {k.siglas}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{fedPorId.get(k.federacionId)?.nombre || '—'}</TableCell>
                        <TableCell>{k.nit || '—'}</TableCell>
                        <TableCell align="right">
                          {fmt(n.afiliados)} / {fmt(k.maxAfiliados)}
                        </TableCell>
                        <TableCell align="right">{moneda(n.aporteMensual)}</TableCell>
                        <TableCell>
                          <Chip size="small" color={colorDocs} label={textoDocs} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={k.activa === false ? 'default' : 'success'} label={k.activa === false ? 'Inactiva' : 'Activa'} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="md" fullWidth>
        {detalle && (
          <>
            <DialogTitle>
              {detalle.nombre}
              <Typography variant="body2" color="text.secondary">
                {[detalle.siglas, fedPorId.get(detalle.federacionId)?.nombre].filter(Boolean).join(' · ')}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ ...grid(200), mb: 3 }}>
                <Dato titulo="NIT" valor={detalle.nit} />
                <Dato titulo="Representante legal" valor={detalle.representanteLegal} />
                <Dato titulo="Cédula del representante" valor={detalle.cedulaRepresentante} />
                <Dato titulo="Correo" valor={detalle.email} />
                <Dato titulo="Teléfono" valor={detalle.telefono} />
                <Dato titulo="Sitio web" valor={detalle.sitioWeb} />
                <Dato titulo="País" valor={nombrePais(detalle.paisId)} />
                <Dato titulo="Dirección" valor={detalle.direccion} />
                <Dato titulo="Constituida" valor={fecha(detalle.fechaConstitucion)} />
                <Dato titulo="Cuenta bancaria" valor={detalle.cuentaBancaria} />
                <Dato
                  titulo="Reparto del wallet"
                  valor={
                    typeof detalle.porcentajeWallet === 'number'
                      ? `${Math.round(detalle.porcentajeWallet * 100)}% asociado · ${Math.round(detalle.porcentajeFederacion * 100)}% federación · ${Math.round(detalle.porcentajeBanco * 100)}% banco`
                      : null
                  }
                />
              </Box>
              {detalle.descripcion && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {detalle.descripcion}
                </Typography>
              )}
              <Typography variant="h6" gutterBottom>
                Documentos
              </Typography>
              {docs.cargando ? (
                <CircularProgress size={22} />
              ) : docs.error ? (
                <Typography color="text.secondary">{docs.error}</Typography>
              ) : docs.items.length === 0 ? (
                <Typography color="text.secondary">Esta cooperativa aún no tiene documentos registrados.</Typography>
              ) : (
                <List disablePadding>
                  {docs.items.map((d) => (
                    <ListItem key={d.id} divider>
                      <ListItemText
                        primary={d.tipoDocumento === 'RUT' ? 'RUT' : 'Cédula del representante legal'}
                        secondary={`${d.nombreArchivo} • registrado el ${fecha(d.creadoEn)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetalle(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Cooperativas;
